import {
  mapValues as lodashMapValues,
  pick as lodashPick,
  pickBy as lodashPickBy,
} from "lodash-es";

import { isFulfilled, isRejected } from "../types/is.ts";
import type {
  DistributedKeys,
  DistributedPick,
  RecordFromArray,
} from "../types/util.ts";

/** Extends map to work with generator iteratees
 *
 * Can be used with an iterable object; or, if applied to an
 * object, will map that object's property entries.
 *
 * E.g.
 * ```
 * const values = mapWith(
 *   someObject,
 *   function *([key, value]) {
 *     if (key.startsWith("a")) yield value * 2)
 *   }
 * );
 * ```
 */
export function mapWith<T, U>(
  input: Iterable<T>,
  iteratee: (item: T) => Generator<U>
): U[];
export function mapWith<T extends object, U>(
  input: T,
  iteratee: (entry: [keyof T, T[keyof T]]) => Generator<U>
): U[];
export function mapWith<T, U>(
  input: Iterable<T> | object,
  iteratee:
    | ((entry: [keyof T, T[keyof T]]) => Generator<U>)
    | ((item: T) => Generator<U>)
) {
  const output: U[] = [];
  const iterable = Symbol.iterator in input ? input : Object.entries(input);
  for (const item of iterable) {
    for (const value of (iteratee as (item: T | [string, any]) => Generator<U>)(
      item
    )) {
      output.push(value);
    }
  }

  return output;
}

/** Type guards properties of an entries array to be defined */
export const isDefinedValue = <K, V>(
  entry: Readonly<[K, V]>
): entry is [K, V & {}] => entry[1] !== undefined;

/** Fixes built-in iteration funtions to preserve type width.
 *
 * Most built-in iteration functions are typed to operate on type V[], rather than
 * type T extends any[]. This means that the result of these functions will be
 * T[number][] when operating on type T. These functions preserve the original type
 * width.
 *
 * Caution: A downside of these utilities is that they will remove any additional
 * properties not numerically indexed. E.g., operating on a type created using
 * `Object.assign(someArray, someObject)` will discard all object properties.
 */
export namespace widetype {
  /** filter, but it does not narrow the collection type */
  export function filter<T extends any[]>(
    collection: T,
    predicate: (value: T[number]) => boolean
  ): T {
    return collection.filter(predicate) as T;
  }

  /** Like array structuring, but does not narrow the collection type */
  export function from<T extends any[]>(...items: T[number][]): T {
    return [...items] as T;
  }

  /** Object.keys, but it assumes readonly types have runtime keys equal to their typing */
  export function keys<
    O extends Readonly<Record<number | string | symbol, any>>,
  >(object: O): (keyof O)[] {
    return Object.keys(object);
  }

  export function values<O extends Readonly<Record<string, any>>>(
    object: O
  ): O[keyof O][] {
    return Object.values(object);
  }

  export function entries<O extends Readonly<Record<string, any>>>(
    object: O
  ): O extends object
    ? { [K in keyof O & string]-?: [K, O[K]] }[keyof O & string][]
    : never {
    // @ts-expect-error library can't deal with distribution
    return Object.entries(object);
  }

  export function fromEntries<A extends (readonly [any, any])[]>(array: A) {
    return Object.fromEntries(array) as RecordFromArray<A>;
  }

  export function mapEntries<
    O extends Readonly<Record<string, any>>,
    K extends string,
    T,
  >(
    object: O,
    iteratee: (key: keyof O, value: O[string]) => [K, T]
  ): { [KK in K]: T } {
    return Object.fromEntries(
      Object.entries(object).map(iteratee as any) as any
    ) as any;
  }

  /** Maps a tuple of property keys to an object with those keys
   *
   * Example:
   * ```
   * const keys = ["apple", "banana", "clementine"] as const;
   *
   * // type is
   * //   {
   * //     apple: number;
   * //     banana: number;
   * //     clementine: number;
   * //   }
   * const fruitLengths = widetype.mapTuple(keys, k => k.length);
   * ```
   */
  export function mapTuple<T extends readonly string[], U>(
    tuple: T,
    mapping: (value: T[number]) => U
  ): {
    [K in T[number]]: U;
  } {
    return Object.fromEntries(tuple.map((v) => [v, mapping(v)])) as {
      [K in T[number]]: U;
    };
  }

  export function mapValues<O extends Readonly<Record<string, any>>, T>(
    object: O,
    iteratee: (value: O[keyof O], key: keyof O) => T
  ): { [K in keyof O]: T } {
    return lodashMapValues(object, iteratee) as { [K in keyof O]: T };
  }

  /** lodash pick, but distributes over type unions */
  export function pick<O extends Record<string, any>, K extends keyof O>(
    object: O,
    ...keys: K[]
  ): DistributedPick<O, K> {
    return lodashPick(object, keys) as any;
  }

  /** lodash pickBy, but maintains key-value type relationship */
  export function pickBy<O extends Record<string, any>>(
    object: O,
    predicate: (value: O[keyof O], key: keyof O) => boolean
  ): Partial<O> {
    return lodashPickBy(object, predicate);
  }

  /** A typesafe version of object dereferencing a union type */
  export function get<
    O extends Record<string, any>,
    K extends DistributedKeys<O>,
  >(object: O, prop: K): K extends keyof O ? O[K] : undefined {
    return object[prop];
  }
}

/** Partitions an array of items with PromiseSettledResult properties
 * into fulfilled and rejected arrays with proper type narrowing.
 */
export function partitionSettledResults<
  T,
  Item extends { result: PromiseSettledResult<T> },
>(
  items: Item[]
): [
  fulfilled: (Item & { result: PromiseFulfilledResult<T> })[],
  rejected: (Item & { result: PromiseRejectedResult })[],
] {
  const fulfilled = items.filter(
    (item): item is Item & { result: PromiseFulfilledResult<T> } =>
      isFulfilled(item.result)
  );
  const rejected = items.filter(
    (item): item is Item & { result: PromiseRejectedResult } =>
      isRejected(item.result)
  );

  return [fulfilled, rejected];
}
